// ========================================
// FACEBOOK OAUTH SERVICE
// ========================================
import { db, auth } from '../../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { oauthConfig, generateOAuthState } from '../../config/oauth.config';
import { getFunctions, httpsCallable } from 'firebase/functions';

export interface FacebookAuthResponse {
  success: boolean;
  profile?: {
    id: string;
    name: string;
    email: string;
  };
  pages?: Array<{ id: string; name: string }>;
  instagramAccounts?: Array<{ id: string; username: string }>;
  error?: string;
}

class FacebookOAuthService {
  private readonly config = oauthConfig.facebook;
  private readonly functions = getFunctions();
  
  /**
   * Step 1: Start OAuth flow - Redirect user to Facebook authorization
   */
  startOAuthFlow(): void {
    const state = generateOAuthState();
    
    console.log('🔐 Starting Facebook OAuth flow...');
    console.log('  - Generated state:', state);
    
    // Store state in sessionStorage for verification in callback
    sessionStorage.setItem('facebook_oauth_state', state);
    sessionStorage.setItem('facebook_oauth_timestamp', Date.now().toString());
    
    // Build authorization URL
    const params = new URLSearchParams({
      client_id: this.config.appId,
      redirect_uri: this.config.redirectUri,
      state: state,
      scope: this.config.scopes.join(','),
      response_type: 'code',
    });

    const authUrl = `${this.config.authUrl}?${params.toString()}`;
    
    console.log('🌐 Redirecting to Facebook:', authUrl);
    
    // Open popup window
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    
    const popup = window.open(
      authUrl,
      'Facebook OAuth',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    if (!popup) {
      console.error('❌ Popup blocked! Please allow popups for this site.');
      throw new Error('Popup blocked. Please allow popups and try again.');
    }

    // Monitor popup
    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        console.log('🔒 Popup closed');
      }
    }, 1000);
  }

  /**
   * Step 2: Handle OAuth callback
   * This is called when Facebook redirects back to our app
   */
  async handleCallback(code: string, state: string): Promise<FacebookAuthResponse> {
    console.log('📥 Facebook callback received', { code: code.substring(0, 10) + '...', state });

    // Verify state to prevent CSRF attacks
    const storedState = sessionStorage.getItem('facebook_oauth_state');
    const timestamp = sessionStorage.getItem('facebook_oauth_timestamp');

    console.log('🔍 Verifying state...', {
      received: state,
      stored: storedState,
      match: state === storedState,
    });

    if (!storedState || state !== storedState) {
      console.error('❌ State mismatch! Possible CSRF attack.');
      throw new Error('Invalid state parameter. Please try again.');
    }

    // Check if state is not too old (5 minutes)
    if (timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age > 5 * 60 * 1000) {
        console.error('❌ State expired');
        throw new Error('OAuth state expired. Please try again.');
      }
    }

    // Clear stored state
    sessionStorage.removeItem('facebook_oauth_state');
    sessionStorage.removeItem('facebook_oauth_timestamp');

    try {
      // Call Cloud Function to exchange code for token
      console.log('☁️ Calling facebookTokenExchange Cloud Function...');
      const tokenExchange = httpsCallable(this.functions, 'facebookTokenExchange');
      
      const result = await tokenExchange({
        code: code,
        redirectUri: this.config.redirectUri,
      });

      const data = result.data as FacebookAuthResponse;
      
      if (data.success) {
        console.log('✅ Facebook connected successfully!', {
          profile: data.profile,
          pagesCount: data.pages?.length || 0,
          instagramCount: data.instagramAccounts?.length || 0,
        });
      }

      return data;

    } catch (error: any) {
      console.error('❌ Facebook token exchange failed:', error);
      throw new Error(error.message || 'Failed to connect Facebook account');
    }
  }

  /**
   * Check if Facebook is connected for current user
   */
  async isConnected(): Promise<boolean> {
    if (!auth.currentUser) return false;

    try {
      const connectionDoc = await getDoc(
        doc(db, 'socialConnections', auth.currentUser.uid)
      );

      if (!connectionDoc.exists()) return false;

      const data = connectionDoc.data();
      return data?.facebook?.connected === true;
    } catch (error) {
      console.error('Error checking Facebook connection:', error);
      return false;
    }
  }

  /**
   * Get Facebook connection details
   */
  async getConnectionDetails() {
    if (!auth.currentUser) return null;

    try {
      const connectionDoc = await getDoc(
        doc(db, 'socialConnections', auth.currentUser.uid)
      );

      if (!connectionDoc.exists()) return null;

      const data = connectionDoc.data();
      return data?.facebook || null;
    } catch (error) {
      console.error('Error getting Facebook connection:', error);
      return null;
    }
  }

  /**
   * Disconnect Facebook account
   */
  async disconnect(): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const userDocRef = doc(db, 'socialConnections', auth.currentUser.uid);
      
      await setDoc(userDocRef, {
        facebook: {
          connected: false,
          disconnectedAt: new Date(),
        },
        instagram: {
          connected: false,
          disconnectedAt: new Date(),
        }
      }, { merge: true });

      console.log('✅ Facebook & Instagram disconnected');
    } catch (error) {
      console.error('❌ Error disconnecting Facebook:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const facebookOAuth = new FacebookOAuthService();





















